import { type Vector } from "../utils/vector";
import { Packet, PacketType } from "./packet";

export enum ArtilleryKind {
    /** a barrage is coming: the zone, when the shells start landing, how long they keep coming, how many */
    Announce,
    /** called off (8 or fewer players left): its zone and markers go */
    Cancel,
    /** shells about to land (where, and in how many ms): the only moment anyone learns where a shell falls */
    Incoming
}

/** a shell about to land: where, and in how many ms */
export interface ArtilleryShell {
    readonly position: Vector
    readonly delay: number
}

/**
 * Artillery barrages (see server/src/artillery.ts). The announcement never says where the shells will fall, only the
 * zone they'll fall in; each shell's spot is sent a moment before it lands, so a player (or a tampered client) can't
 * pick a safe spot from it.
 */
export interface ArtilleryData {
    readonly type: PacketType.Artillery
    readonly id: number
    readonly kind: ArtilleryKind
    readonly center: Vector
    readonly radius: number
    /** ms from now until the first shell lands */
    readonly landsIn: number
    /** the shells keep landing for this long (ms) */
    readonly spread: number
    readonly count: number
    /** the bomber's heading (radians) */
    readonly heading: number
    readonly shells: readonly ArtilleryShell[]
}

const clamp16 = (value: number): number => Math.max(0, Math.min(65535, Math.round(value)));

export const ArtilleryPacket = new Packet<ArtilleryData>(PacketType.Artillery, {
    serialize(stream, data) {
        stream.writeUint8(data.id & 0xff);
        stream.writeUint8(data.kind);
        if (data.kind === ArtilleryKind.Announce) {
            stream.writePosition(data.center);
            stream.writeFloat32(data.radius);
            stream.writeUint16(clamp16(data.landsIn));
            stream.writeUint16(clamp16(data.spread));
            stream.writeUint8(Math.min(255, data.count));
            stream.writeRotation(data.heading);
        } else if (data.kind === ArtilleryKind.Incoming) {
            const shells = data.shells.slice(0, 255);
            stream.writeUint8(shells.length);
            for (const shell of shells) {
                stream.writePosition(shell.position);
                stream.writeUint16(clamp16(shell.delay));
            }
        }
    },
    deserialize(stream, data) {
        data.id = stream.readUint8();
        data.kind = stream.readUint8() as ArtilleryKind;
        data.center = { x: 0, y: 0 };
        data.radius = 0;
        data.landsIn = 0;
        data.spread = 0;
        data.count = 0;
        data.heading = 0;
        data.shells = [];
        if (data.kind === ArtilleryKind.Announce) {
            data.center = stream.readPosition();
            data.radius = stream.readFloat32();
            data.landsIn = stream.readUint16();
            data.spread = stream.readUint16();
            data.count = stream.readUint8();
            data.heading = stream.readRotation();
        } else if (data.kind === ArtilleryKind.Incoming) {
            const count = stream.readUint8();
            const shells: ArtilleryShell[] = [];
            for (let i = 0; i < count; i++) shells.push({ position: stream.readPosition(), delay: stream.readUint16() });
            data.shells = shells;
        }
    }
});
